import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, Clock, BookOpen } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { getBlog } from "@/data/blogContent";

const BACK_LABEL = {
  sk: "Späť na úvod",
  en: "Back to home",
  cs: "Zpět na úvod",
  de: "Zurück zur Startseite",
  pl: "Powrót do strony głównej",
};

const OUTRO = {
  sk: { tag: "Pridaj sa do komunity tých, ktorí rozumejú", reg: "Zaregistrovať sa", chat: "Komunitný chat" },
  en: { tag: "Join the community of those who understand", reg: "Sign up", chat: "Community chat" },
  cs: { tag: "Přidej se ke komunitě těch, kteří rozumí", reg: "Zaregistrovat se", chat: "Komunitní chat" },
  de: { tag: "Tritt der Community jener bei, die es verstehen", reg: "Registrieren", chat: "Community-Chat" },
  pl: { tag: "Dołącz do społeczności tych, którzy rozumieją", reg: "Zarejestruj się", chat: "Czat społeczności" },
  fr: {
  tag: "Rejoins la communauté de ceux qui comprennent",
  reg: "S'inscrire",
  chat: "Chat communautaire"
},
};

export default function PersonaBlog() {
  const { slug } = useParams();
  const { lang } = useLang();
  const post = getBlog(slug, lang);
  if (!post) return <Navigate to="/" replace />;
  const Icon = post.icon;
  const back = BACK_LABEL[lang] || BACK_LABEL.sk;
  const outro = OUTRO[lang] || OUTRO.sk;

  return (
    <div data-testid={`blog-${slug}`} className="max-w-4xl mx-auto px-4 md:px-6 py-10">
      <Link to="/" className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-zinc-500 hover:text-gold-400 mb-6">
        <ArrowLeft className="w-3.5 h-3.5" /> {back}
      </Link>

      <div className="relative h-64 md:h-80 mb-8 overflow-hidden border border-zinc-800 bg-gradient-to-br from-zinc-900 via-zinc-950 to-black">
        <img
          src={post.cover}
          alt={post.title}
          className="absolute inset-0 w-full h-full object-cover"
          onError={(e) => { e.target.style.display = "none"; }}
        />
        <div className="absolute inset-0 wb-grid-bg opacity-20 pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <div className="flex items-center gap-2 mb-2">
            <span className="wb-tag !text-gold-400 !border-gold-600 inline-flex items-center gap-1.5">
              <Icon className="w-3.5 h-3.5" /> BLOG
            </span>
            <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 inline-flex items-center gap-1">
              <Clock className="w-3 h-3" /> {post.readTime}
            </span>
          </div>
          <h1 className="font-display font-black text-3xl md:text-5xl text-white leading-tight">{post.title}</h1>
          <p className="text-zinc-400 text-sm md:text-base mt-2">{post.subtitle}</p>
        </div>
      </div>

      <article className="space-y-7 prose-tigik">
        {post.paragraphs.map((par, i) => (
          <section key={i}>
            <h2 className="font-display font-bold text-xl md:text-2xl text-gold-300 mb-2 flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-gold-600" />
              {par.h}
            </h2>
            <p className="text-sm md:text-base text-zinc-300 leading-relaxed whitespace-pre-wrap">{par.p}</p>
          </section>
        ))}
      </article>

      <div className="mt-12 pt-8 border-t border-zinc-800 text-center">
        <p className="text-xs font-mono uppercase tracking-wider text-zinc-600 mb-4">{outro.tag}</p>
        <div className="flex flex-wrap gap-3 justify-center">
          <Link to="/register" className="wb-btn-primary px-6 py-3 text-sm">{outro.reg}</Link>
          <Link to="/community" className="wb-btn-ghost px-6 py-3 text-sm">{outro.chat}</Link>
        </div>
      </div>
    </div>
  );
}
